import { useEffect, useState, useCallback } from 'react'
import { Banknote, FileText, Check, X, Download, Filter, CheckCheck } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dth = (s) => (s ? new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const ST_LABEL = { em_analise: 'Em Análise', aprovado: 'Aprovado', pago: 'Pago', rejeitado: 'Rejeitado' }
const ST_COR = { em_analise: 'bg-amber-100 text-amber-700', aprovado: 'bg-blue-100 text-blue-700', pago: 'bg-emerald-100 text-emerald-700', rejeitado: 'bg-red-100 text-red-600' }

export default function GestaoSaques() {
  const [stats, setStats] = useState(null)
  const [saques, setSaques] = useState([])
  const [status, setStatus] = useState('em_analise')
  const [sel, setSel] = useState([])
  const [loading, setLoading] = useState(true)

  const carregar = useCallback(() => {
    setLoading(true); setSel([])
    Promise.all([
      api.get('/financeiro/saques/stats').then((r) => setStats(r.data)).catch(() => {}),
      api.get(`/financeiro/saques?limit=100${status ? '&status=' + status : ''}`).then((r) => setSaques(r.data.data || [])).catch(() => setSaques([])),
    ]).finally(() => setLoading(false))
  }, [status])
  useEffect(() => { carregar() }, [carregar])

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const toggleAll = () => setSel(sel.length === saques.length ? [] : saques.map((s) => s.id))

  const aprovar = (id) => api.post(`/financeiro/saques/${id}/aprovar`).then(carregar).catch(() => errorModal('Erro.'))
  const rejeitar = async (id) => {
    if (await confirmModal('Rejeitar este saque? O valor volta ao saldo disponível do licenciado.'))
      api.post(`/financeiro/saques/${id}/rejeitar`, { motivo: 'Documentação/NF inválida' }).then(carregar).catch(() => errorModal('Erro.'))
  }
  const pagar = async (id) => { if (await confirmModal('Confirmar pagamento deste saque?')) api.post(`/financeiro/saques/${id}/pagar`).then(carregar).catch(() => errorModal('Erro.')) }

  const lote = async (acao) => {
    if (!sel.length) return errorModal('Selecione ao menos um saque.')
    const txt = { aprovar: 'aprovar', rejeitar: 'rejeitar', pagar: 'pagar' }[acao]
    if (!(await confirmModal(`Confirmar ${txt} ${sel.length} saque(s) em lote?`))) return
    api.post('/financeiro/saques/lote', { ids: sel, acao, motivo: acao === 'rejeitar' ? 'Rejeição em lote' : null })
      .then((r) => { successModal(`${r.data.processados} processado(s), ${r.data.ignorados} ignorado(s).`); carregar() })
      .catch(() => errorModal('Erro no lote.'))
  }

  const remessa = () => {
    api.get(`/financeiro/saques/remessa?status=aprovado`).then((r) => {
      if (!r.data.quantidade) return errorModal('Nenhum saque aprovado para remessa.')
      const blob = new Blob([r.data.csv], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
      a.download = `remessa_saques_${new Date().toISOString().slice(0, 10)}.csv`; a.click()
      successModal(`Remessa gerada: ${r.data.quantidade} saque(s), total ${money(r.data.total)}.`)
    }).catch(() => errorModal('Erro ao gerar remessa.'))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow"><Banknote size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Gestão de Saques</h1>
            <p className="text-sm text-gray-500">Documentação fiscal, aprovação em lote e arquivo de remessa.</p>
          </div>
        </div>
        <button onClick={remessa} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold"><Download size={16} /> Gerar Remessa (aprovados)</button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Mini label="Em Análise" value={money(stats.em_analise)} sub={`${stats.qtd_em_analise} saque(s)`} cor="text-amber-600" />
          <Mini label="Aprovado" value={money(stats.aprovado)} sub={`${stats.qtd_aprovado} saque(s)`} cor="text-blue-600" />
          <Mini label="Pago" value={money(stats.pago)} sub={`${stats.qtd_pago} saque(s)`} cor="text-emerald-600" />
          <Mini label="Rejeitado" value={money(stats.rejeitado)} cor="text-red-600" />
          <Mini label="Saques do mês" value={money(stats.saques_mes)} cor="text-gray-700" />
        </div>
      )}

      <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 flex-wrap">
        <Filter size={15} className="text-gray-400" />
        {['em_analise', 'aprovado', 'pago', 'rejeitado', ''].map((s) => (
          <button key={s || 'todos'} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${status === s ? 'bg-gray-900 text-white' : 'bg-gray-50 border border-gray-200 text-gray-600'}`}>{s ? ST_LABEL[s] : 'Todos'}</button>
        ))}
        {sel.length > 0 && (
          <div className="ml-auto flex gap-2">
            <span className="text-xs text-gray-500 self-center">{sel.length} selecionado(s):</span>
            <button onClick={() => lote('aprovar')} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700">Aprovar</button>
            <button onClick={() => lote('pagar')} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700">Pagar</button>
            <button onClick={() => lote('rejeitar')} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-600">Rejeitar</button>
          </div>
        )}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader text="Carregando saques..." /></div> : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2"><input type="checkbox" checked={sel.length === saques.length && saques.length > 0} onChange={toggleAll} /></th>
                <th className="px-3 py-2">Licenciado</th><th className="px-3 py-2">CPF</th><th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2">Chave PIX</th><th className="px-3 py-2">NF</th><th className="px-3 py-2">Solicitado</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {saques.map((s) => (
                <tr key={s.id} className="border-t border-gray-100 hover:bg-primary-50/30">
                  <td className="px-3 py-2"><input type="checkbox" checked={sel.includes(s.id)} onChange={() => toggle(s.id)} /></td>
                  <td className="px-3 py-2 font-medium text-gray-900">{s.licenciado_nome || `#${s.licenciado_id}`}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{s.cpf || '—'}</td>
                  <td className="px-3 py-2 text-right font-bold">{money(s.valor)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{s.chave_pix || '—'}</td>
                  <td className="px-3 py-2">
                    {s.tem_nf ? <a href={s.nf_arquivo} target="_blank" rel="noreferrer" className="text-accent-600 flex items-center gap-1 text-xs"><FileText size={13} /> {s.nf_numero || 'ver'}</a> : <span className="text-red-400 text-xs">sem NF</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{dth(s.solicitado_em)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${ST_COR[s.status]}`}>{ST_LABEL[s.status] || s.status}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {s.status === 'em_analise' && <button onClick={() => aprovar(s.id)} title="Aprovar" className="p-1.5 rounded-lg bg-blue-50 text-blue-600"><Check size={14} /></button>}
                      {(s.status === 'em_analise' || s.status === 'aprovado') && <button onClick={() => pagar(s.id)} title="Pagar" className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600"><CheckCheck size={14} /></button>}
                      {(s.status === 'em_analise' || s.status === 'aprovado') && <button onClick={() => rejeitar(s.id)} title="Rejeitar" className="p-1.5 rounded-lg bg-red-50 text-red-500"><X size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {saques.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Nenhum saque {status ? `(${ST_LABEL[status]})` : ''}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Mini({ label, value, sub, cor }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <p className={`text-lg font-heading font-bold ${cor}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
    </div>
  )
}
